'use client';

import { OrderSubscriber } from '@drift-labs/sdk';
import { usePathname } from 'next/navigation';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';

const INITIAL_CONTEXT_VALUE = { orderSubscriber: undefined } as {
	orderSubscriber?: OrderSubscriber;
};

export const OrderSubscriberContext = React.createContext<{
	orderSubscriber?: OrderSubscriber;
}>({ orderSubscriber: undefined });

const OrderSubscriberProvider = (props: PropsWithChildren<any>) => {
	const connection = useDriftStore((s) => s.connection.current);
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const pathname = usePathname();

	const [contextValue, setContextValue] = useState(INITIAL_CONTEXT_VALUE);

	// Check if current route needs order subscription
	const needsOrders = pathname
		? needsDataRequirement(pathname, 'trading')
		: false;

	useEffect(() => {
		if (!connection || !driftClient) {
			setContextValue(INITIAL_CONTEXT_VALUE);
			return;
		}

		// Only create order subscriber if route requires it
		if (!needsOrders) {
			setContextValue(INITIAL_CONTEXT_VALUE);
			return;
		}

		const orderSubscriber = new OrderSubscriber({
			driftClient,
			subscriptionConfig: {
				type: 'websocket',
				skipInitialLoad: true,
				commitment: Env.defaultCommitmentLevel,
			},
		});

		setContextValue({
			orderSubscriber,
		});

		return () => {
			// Unsubscribe when we create a new subscriber
			orderSubscriber.usersAccounts?.clear();
			orderSubscriber.unsubscribe();
		};
	}, [connection, driftClient, needsOrders]);

	return (
		<OrderSubscriberContext value={contextValue}>
			{props.children}
		</OrderSubscriberContext>
	);
};

export default OrderSubscriberProvider;
